import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createCheckoutSession } from '@/api/billing';
import PageHeader from '@/ui/organisms/PageHeader';
import Card from '@/ui/atoms/Card';
import Button from '@/ui/atoms/Button';
import Eyebrow from '@/ui/atoms/Eyebrow';

// Pricing · M0 single SKU $19/month via Lemon Squeezy.
// PR-UX-04 · loading checkout state surfaced via Button loading prop.
// PR-UX-10 flow · checkout opens LS overlay (not external) to preserve domain context.
//
// Intent preservation (per EXPERIENCE.md derived gap #1):
//   Unauthed click → /auth + sessionStorage flag
//   Post-auth landing → /pricing?subscribe=1 → auto-fire checkout
const SS_AUTOSUB_KEY = 'fyd_pricing_autosub';

export default function Pricing() {
  const { t } = useTranslation();
  const { isSignedIn, isLoaded } = useUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFiredRef = useRef(false);

  const startCheckout = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await createCheckoutSession();
      window.location.href = r.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setBusy(false);
    }
  };

  // Auto-fire path: returning here from /auth with `subscribe=1`.
  useEffect(() => {
    if (!isLoaded || autoFiredRef.current) return;
    const wantsAutoSub = params.get('subscribe') === '1' || sessionStorage.getItem(SS_AUTOSUB_KEY) === '1';
    if (!wantsAutoSub) return;
    sessionStorage.removeItem(SS_AUTOSUB_KEY);
    if (isSignedIn) {
      autoFiredRef.current = true;
      // Strip the flag from URL so refresh doesn't loop.
      const next = new URLSearchParams(params);
      next.delete('subscribe');
      setParams(next, { replace: true });
      void startCheckout();
    }
  }, [isLoaded, isSignedIn, params, setParams]);

  const onSubscribe = async () => {
    if (!isSignedIn) {
      // Persist intent through Clerk's internal routing → /auth/callback → here.
      // SS_AUTOSUB_KEY arms the auto-fire on next mount; fyd_post_auth_redirect
      // tells AuthCallback where to send us (Clerk drops URL query params).
      sessionStorage.setItem(SS_AUTOSUB_KEY, '1');
      sessionStorage.setItem('fyd_post_auth_redirect', '/pricing?subscribe=1');
      navigate(`/auth?redirect_url=${encodeURIComponent('/pricing?subscribe=1')}`);
      return;
    }
    void startCheckout();
  };

  return (
    <section className="max-w-page mx-auto px-8 lg:px-16 xl:px-24 py-24">
      <PageHeader
        eyebrow={t('pricing.eyebrow')}
        title={t('pricing.title')}
        sub={t('pricing.sub')}
      />

      <Card padding="loose" className="max-w-md">
        <Eyebrow tone="ash" className="mb-2">{t('pricing.plan')}</Eyebrow>
        <div className="font-serif text-3xl font-medium mb-2">{t('pricing.planName')}</div>
        <div className="font-light text-6xl tracking-tight text-ash-700 mb-1">
          <sup className="text-2xl font-light">$</sup>19
          <span className="text-sm text-ink-3 ml-1.5 font-normal">{t('pricing.perMonth')}</span>
        </div>
        <ul className="my-6 space-y-3 text-sm">
          <li>✓ {t('pricing.feat1')}</li>
          <li>✓ {t('pricing.feat2')}</li>
          <li>✓ {t('pricing.feat3')}</li>
          <li>✓ {t('pricing.feat4')}</li>
        </ul>
        <Button variant="primary" block onClick={onSubscribe} loading={busy}>
          {isSignedIn ? t('pricing.cta') : t('pricing.ctaSignIn')}
        </Button>
        {error && <p className="text-sm text-vermilion-700 mt-3">{error}</p>}
      </Card>

      <p className="text-[13px] text-ink-3 mt-10 max-w-md">
        {t('pricing.legal')}
      </p>
    </section>
  );
}
